/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable @typescript-eslint/no-use-before-define */
import './editCreditCard.css';

import MUISelectInput from '@brands/Components/Inputs/MuiSelectInput/MUISelectInput';
import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import ProgressBar from '@brands/Components/ProgressBar/ProgressBar';
import { useWindowSize } from '@brands/hooks';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import {
  getPatientReviewProfileRoleSegment,
  getStaffReviewProfileBasePath,
  isStaffCaseCreationRole,
} from '@brands/Utils/reviewProfileBasePath';
import { getOneCase } from '@brands/services/cases/getCaseById';
import { Pricing } from '@brands/services/cases/types/ICase';
import { Address, UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectOrganizationPrices } from '@brands/store/selectors/organizationPrices';
import { selectPatient } from '@brands/store/selectors/selectedPatient';
import { updatePaymentAvailability } from '@brands/Utils/getFriendlyName';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import Modal, { Styles } from 'react-modal';
import { useBeforeUnload, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import Button from '../../../../Components/Button/Patient/Button';
import { config } from '../../../../config/config';
import { CaseType, ICase } from '../../../../services/cases/types/ICase';
import { addPaymentProfile } from '../../../../services/identity/addPaymentProfile';
import { PaymentProfile } from '../../../../services/identity/types/PaymentProfile';
import { updatePaymentProfile } from '../../../../services/identity/updatePaymentProfile';
import { customStylesDashboard } from '../../../../Utils/customStyles';
import { displayErrorDetails } from '../../../../Utils/displayError';
import { stateOptions } from '../../../../Utils/selectOptions';
import UnsavedChangesModal from '../../../Dashboard/Components/UnsavedChangesModal/UnsavedChangesModal';
import closeICon from '../../../Dashboard/Patient/components/closeIconSVG.svg';
import {
  BillingPostalCodeMismatchError,
  resolveBillingPostalCode,
  tokenizeAndVerifyBillingPostalCode,
  updateDataPayment,
} from '../../../MyProfile/Patient/utils/utilsFunctions';
import { PatientFormFiveSubmit } from '../../../PatientForms/PatientForm/utils/types';
import styles from './styles.module.scss';
import { validationSchema } from './validationSchema';

const { locationId } = config.square;
const appId = config.square.applicationId;

const MAX_RETRIES = 3; // Set the maximum number of retry attempts
const RETRY_INTERVAL = 2000; // Set the interval between retry attempts in milliseconds

type Card = {
  attach: (containerId: string) => Promise<any>;
  tokenize: () => Promise<{
    token: string;
    status: string;
    errors?: JSON;
    details?: {
      card: any; // Replace "any" with the type of the card object
      method: string;
      billing: any; // Replace "any" with the type of the billing object
    };
  }>;
  addEventListener: (eventName: string, callback: () => void) => void;
};

interface IModal {
  setOpenModal: (arg0: boolean) => void;
  isOpen: boolean;
  thisCase: ICase;
  reloadAll: () => Promise<void>;
  refreshAll: () => Promise<void>;
}

const EditCreditCardModal = ({ setOpenModal, isOpen, thisCase }: IModal): JSX.Element => {
  const screenSize = useWindowSize();
  const navigate = useNavigate();
  const isMobile = useMemo(() => screenSize.width < 768, [screenSize.width]);
  const [loading, setLoading] = useState<boolean>(false);
  const [disablePaymentButton, setDisablePaymentButton] = useState(false);
  const [isLoadingSquare, setIsLoadingSquare] = useState(true);
  const [hasUnsavedChangesModal, setHasUnsavedChangesModal] = useState(false);
  const [card, setCard] = useState<Card>();
  const { userInfo } = useAppSelector(selectAuth);
  const { selectedPatient } = useAppSelector(selectPatient);
  const [cardInitialized, setCardInitialized] = useState(false);
  const currentOrganizationPrices = useAppSelector(selectOrganizationPrices);
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<PatientFormFiveSubmit>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema()),
  });
  useBeforeUnload((event) => {
    if (hasChanges) {
      event.preventDefault();
      setHasUnsavedChangesModal(true);
    }
  });

  const hasChanges = useMemo(() => {
    return Object.keys(watch()).some((key) => {
      const fieldKey = key as keyof PatientFormFiveSubmit;
      return watch(fieldKey) !== '';
    });
  }, [watch()]);

  const mobileCustomStylesDashboard: Styles = {
    ...customStylesDashboard,
    overlay: {
      ...(customStylesDashboard?.overlay || {}),
      overflowY: 'scroll' as const,
      overflowX: 'hidden' as const,
    },
    content: {
      ...(customStylesDashboard?.content || {}),
      width: '100vw',
      height: '100vh',
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      right: '0',
      bottom: '0',
      padding: '90px 16px 32px 16px',
      margin: '0',
      borderRadius: '0',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      overflowY: 'scroll' as const,
    },
  };

  const customStylesDashboardDesktop: Styles = {
    ...customStylesDashboard,
    overlay: {
      ...customStylesDashboard.overlay,
    },
    content: {
      ...customStylesDashboard.content,
      flexDirection: 'column',
      width: '444px',
      padding: '55px 32px 32px',
      overflowY: 'auto',
      maxHeight: '850px',
      justifyContent: 'flex-start',
    },
  };

  const onInvalid = (err: any): void => {
    const error = Object.values(err)[0] as any;
    if (error) {
      toast.error(error.message as string);
    }
  };
  const squareDiv = document.getElementById('edit-card-container');

  useEffect(() => {
    const initSquare = async (): Promise<void> => {
      if (!squareDiv || !window.Square) throw new Error('Square.js failed to load properly');

      const payments = window.Square.payments(appId, locationId);
      try {
        const paymentCard = await payments.card();
        if (squareDiv.childNodes.length === 0) {
          await paymentCard.attach('#edit-card-container');
        }
        setCard(paymentCard);
        paymentCard.addEventListener('errorClassAdded', () => setDisablePaymentButton(true));
        paymentCard.addEventListener('errorClassRemoved', () => setDisablePaymentButton(false));
        setIsLoadingSquare(false);
        setCardInitialized(true);
      } catch (error: unknown) {
        displayErrorDetails(error);
      }
    };

    initSquare();
  }, [squareDiv]);

  const onSubmit = async (data: PatientFormFiveSubmit): Promise<void> => {
    if (hasUnsavedChangesModal) setHasUnsavedChangesModal(false);
    const updatedCase = await getOneCase(thisCase.id);
    if (updatedCase.type !== 'video_call_scheduled' && thisCase.type !== CaseType.phone_call_scheduled) {
      toast.error(
        'You are no longer able to update the payment for this consultation because the consultation type has changed.',
      );
    } else if (updatePaymentAvailability(updatedCase)) {
      setLoading(true);
      setDisablePaymentButton(true);
      let paymentId;
      if (
        card &&
        cardInitialized &&
        Array.isArray(currentOrganizationPrices.currentOrganizationPrices) &&
        currentOrganizationPrices.currentOrganizationPrices
          ?.find((pricing: Pricing) => Array.isArray(pricing.case_types) && pricing.case_types.includes(thisCase.type))
          ?.payment_options?.includes('Credit Card')
      ) {
        try {
          const tokenResult = await tokenizeAndVerifyBillingPostalCode(
            card,
            resolveBillingPostalCode(data, userInfo, true),
          );
          if (tokenResult.status === 'OK') {
            const casePatientId = Number(updatedCase.patient.id);
            const paymentProfileOwnerId =
              userInfo.role.name === UserRoleName.Collector
                ? casePatientId
                : Number(
                    userInfo?.id === updatedCase.patient.id
                      ? updatedCase.patient.id
                      : updatedCase.patient.guardian_id,
                  );
            const billingSelectedPatientId =
              userInfo.role.name === UserRoleName.Collector ? casePatientId : Number(userInfo?.id);

            const dataWithUpdatedPayment = updateDataPayment(
              data,
              tokenResult,
              userInfo,
              true,
              updatedCase.patient.address as Address,
              billingSelectedPatientId,
              paymentProfileOwnerId,
            );
            const res = await addPaymentProfile(dataWithUpdatedPayment as PaymentProfile, userInfo.role.name);
            paymentId = res.id!;
            await updatePaymentProfile(updatedCase.id, {
              payment_profile_id: paymentId,
            });
            setOpenModal(false);
          } else {
            toast.error('Please Enter your Credit Card information correctly');
          }
        } catch (error: unknown) {
          displayErrorDetails(error);
          if (!(error instanceof BillingPostalCodeMismatchError)) {
            setOpenModal(false);
          }
        } finally {
          setDisablePaymentButton(false);
          setLoading(false);
        }
      } else {
        toast.error("You can't update your payment information.");
        setDisablePaymentButton(false);
        setLoading(false);
      }
    } else {
      toast.error("You can't update your payment information up to 1 hour before your scheduled appointment.");
    }
  };

  const closeModal = (): void => {
    if (hasChanges) {
      setHasUnsavedChangesModal(true);
    } else {
      if (
        userInfo.role.name !== 'Patient' &&
        selectedPatient?.id != null &&
        isStaffCaseCreationRole(userInfo.role.name)
      ) {
        navigate(
          `${getStaffReviewProfileBasePath(userInfo.role.name)}/${getPatientReviewProfileRoleSegment(
            selectedPatient,
          )}/${selectedPatient.id}`,
        );
      }
      setOpenModal(false);
    }
  };

  const formRef = useRef<HTMLFormElement | null>(null);

  const triggerSubmit = (): void => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      style={isMobile ? mobileCustomStylesDashboard : customStylesDashboardDesktop}
      contentLabel="Modal"
      ariaHideApp={false}
    >
      {isMobile ? (
        <div className={styles.backDiv}>
          <ProgressBar currentStep={1} totalSteps={1} onBack={closeModal} hideStepNumbers />
        </div>
      ) : (
        <div className={styles.closeICon}>
          <img src={closeICon} onClick={() => closeModal()} alt="close" />
        </div>
      )}
      <form
        ref={formRef}
        className={`${styles.editCCModalDiv} ${styles.formContainer}`}
        onSubmit={handleSubmit(onSubmit, onInvalid)}
      >
        <div className={styles.body}>
          <div className={styles.subTitle}>Edit payment method</div>
          <div className={styles.inputWrap}>
            <TextField
              {...register('creditCardFullName')}
              errors={errors.creditCardFullName ? errors : {}}
              label="Name as it appears on card"
              required
              name="creditCardFullName"
              value={getValues('creditCardFullName')}
              onChange={(e) => {
                setValue('creditCardFullName', e.target.value, { shouldValidate: true });
              }}
              inputClass={`${styles.profileInputClass} fs-exclude`}
              wrapperClassName={`fs-unmask ${styles.editCreditCardFullClass}`}
            />
          </div>
          {isLoadingSquare && (
            <div>
              <Loading dotted />{' '}
            </div>
          )}
          <div id="payment-form">
            <div id="edit-card-container" />
          </div>
          <div className={`fs-unmask ${styles.subTitle} ${styles.subTitleMarginTop}`}>Billing Address</div>
          <div className={styles.inputWrap}>
            <TextField
              {...register('creditCardAddress1')}
              errors={errors.creditCardAddress1 ? errors : {}}
              label="Street address"
              required
              name="creditCardAddress1"
              value={getValues('creditCardAddress1')}
              onChange={(e) => {
                setValue('creditCardAddress1', e.target.value, { shouldValidate: true });
              }}
              inputClass={`${styles.profileInputClass} fs-exclude`}
              wrapperClassName={`fs-unmask ${styles.editCreditCardClass}`}
            />
          </div>
          <div className={styles.inputWrap}>
            <TextField
              {...register('creditCardAddress2')}
              errors={errors.creditCardAddress2 ? errors : {}}
              label="Apt/Suite"
              name="creditCardAddress2"
              value={getValues('creditCardAddress2')}
              onChange={(e) => {
                setValue('creditCardAddress2', e.target.value, { shouldValidate: true });
              }}
              inputClass={`${styles.profileInputClass} fs-exclude`}
              wrapperClassName={`fs-unmask ${styles.editCreditCardClass}`}
            />
          </div>
          <div className={styles.inputWrap}>
            <TextField
              {...register('creditCardZipCode')}
              errors={errors.creditCardZipCode ? errors : {}}
              label="Zip code"
              required
              name="creditCardZipCode"
              value={getValues('creditCardZipCode')}
              onChange={(e) => {
                setValue('creditCardZipCode', e.target.value, { shouldValidate: true });
              }}
              inputClass={`${styles.profileInputClass} fs-exclude`}
              wrapperClassName={`fs-unmask ${styles.editCreditCardClass}`}
            />
          </div>
          <div className={styles.rowDiv}>
            <div className={styles.inputWrap}>
              <TextField
                {...register('creditCardCity')}
                errors={errors.creditCardCity ? errors : {}}
                label="City"
                required
                name="creditCardCity"
                value={getValues('creditCardCity')}
                onChange={(e) => {
                  setValue('creditCardCity', e.target.value, { shouldValidate: true });
                }}
                inputClass={`${styles.profileInputClass} fs-exclude`}
                wrapperClassName={`fs-unmask ${styles.editCreditCardClass}`}
              />
            </div>
            <div className={styles.inputWrap} data-validate="Choose State">
              <MUISelectInput
                {...register('creditCardState')}
                label="State"
                errors={errors.creditCardState ? errors : {}}
                name="creditCardState"
                onChange={(value: any) => {
                  setValue('creditCardState', value as string, { shouldValidate: true });
                }}
                value={watch('creditCardState') || ''}
                required
                inputClass={`${styles.profileInputClass} fs-exclude`}
                wrapperClassName={`fs-unmask ${styles.editCreditCardClass}`}
                options={stateOptions}
              />
            </div>
          </div>

          <div className={styles.modalButtons}>
            <Button
              variant="contained"
              disabled={loading || disablePaymentButton || !hasChanges}
              type="submit"
              className={`fs-unmask ${styles.save}`}
            >
              Save changes
            </Button>
            <Button
              type="button"
              variant="text"
              onClick={() => {
                setOpenModal(false);
              }}
              className={`fs-unmask ${styles.cancel}`}
            >
              Cancel
            </Button>
          </div>
        </div>
      </form>
      {hasUnsavedChangesModal && (
        <UnsavedChangesModal
          isOpen={hasUnsavedChangesModal}
          closeParentModal={setOpenModal}
          toggleModal={setHasUnsavedChangesModal}
          saveChange={triggerSubmit}
          loading={loading}
          hideModal={() => setHasUnsavedChangesModal(false)}
        />
      )}
    </Modal>
  );
};
export default EditCreditCardModal;
